import { useEffect, useRef } from "react";
import { GfgTextField } from "@/components/ui/GfgTextField";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import type { AddressParts } from "@/interfaces/redemption";
import { loadGooglePlaces, parsePlace } from "@/utils/googleMaps";

interface RedemptionAddressAutocompleteProps {
  value: string;
  error?: boolean;
  /** Error message (shown + announced when `error` is true). */
  errorText?: string;
  onChange: (street: string) => void;
  /** Fired when a Google suggestion is chosen — fills street/city/state/zip together. */
  onSelect: (parts: AddressParts) => void;
}

/**
 * Recipient address field with Google Places autocomplete (RFW address flow). The
 * Google Maps key comes from `GET /api/config`; the SDK is loaded on demand. If it
 * can't load (no key, or a host not allow-listed for the key), this degrades to a
 * plain address input — autocomplete is a progressive enhancement, and Smarty still
 * verifies whatever the recipient ends up entering.
 */
export default function RedemptionAddressAutocomplete({
  value,
  error = false,
  errorText,
  onChange,
  onSelect,
}: RedemptionAddressAutocompleteProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const { data: config } = usePublicConfig();
  const apiKey = config?.googleMapsApiKey;
  // Keep the latest onSelect without re-running the (one-shot) SDK-attach effect.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    loadGooglePlaces(apiKey).then((places) => {
      if (cancelled || !places) return; // null → plain input (graceful fallback)
      const input = wrapRef.current?.querySelector("input");
      if (!input) return;
      const ac = new places.Autocomplete(input, {
        types: ["address"],
        componentRestrictions: { country: "us" },
        fields: ["address_components"],
      });
      ac.addListener("place_changed", () => {
        const parts = parsePlace(ac.getPlace());
        if (parts) onSelectRef.current(parts);
      });
    });
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  return (
    <div ref={wrapRef}>
      <GfgTextField
        id="iv-address"
        label="Address"
        autoComplete="address-line1"
        ignorePasswordManagers
        value={value}
        error={error}
        errorText={errorText}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
